import React, { useState, useEffect, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ActionButton } from 'components/modals';
import { SyutTreeGrid, type TreeGridColumn } from 'components/ui';
import { EnterpriseScreenShell, actionButtonFactory } from 'components/enterprise-shell';
import notificationService from 'services/notificationService';
import { accountTypeService, reportClassService, accountSubtypeService } from 'api/services';

// ============================================================================
// ACCOUNT TYPES (Settings) — the classification every ledger sits under, as one
// read-only tree: account type → report class → subtype. Product reference data
// (seed/100 and later seeds), shared by every entity of the company; groups and
// ledgers are classified under a subtype. Not edited here: ledgers, report classes
// and the financial statements point at these rows.
// ============================================================================

interface Node {
  id: string;
  parentId: string | null;
  level: 'type' | 'class' | 'subtype';
  code: string;
  name: string;
  normalBalance: string;
  statement: string;
  section: string;
  appliesTo: string;
  sortKey: number;
}

export const statementLabel = (t?: string | null) => (!t ? '' : t === 'BALANCE_SHEET' ? 'Balance sheet'
  : /PROFIT|INCOME/.test(t) ? 'Profit and loss' : t.charAt(0) + t.slice(1).toLowerCase().replace(/_/g, ' '));
export const humanize = (s?: string | null) => (!s ? '' : s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' '));
/** Where a subtype applies: "All", or its countries and legal forms (ADR-0057). */
export const appliesTo = (countries?: string[] | null, forms?: string[] | null) => {
  const parts = [countries?.length ? countries.join(', ') : '', forms?.length ? forms.map(humanize).join(', ') : ''].filter(Boolean);
  return parts.length ? parts.join(' · ') : 'All';
};

interface Props { moduleIcon?: string; tabId?: string }

const AccountTypesScreen: React.FC<Props> = ({ moduleIcon, tabId }) => {
  const [nodes, setNodes] = useState<Node[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  // Default: what the selected entity sees (its country and legal form); "All countries" shows the whole superset.
  const [allCountries, setAllCountries] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [types, classes, subtypes] = await Promise.all([
        accountTypeService.getAll({ pageSize: 100 }),
        reportClassService.getAll({ pageSize: 100 }),
        accountSubtypeService.getAll({ pageSize: 100, allCountries }),
      ]);
      const classIds = new Set(classes.data.map(c => c.id));
      setNodes([
        ...types.data.map(t => ({
          id: t.id, parentId: null, level: 'type' as const, code: t.typeCode, name: t.typeName,
          normalBalance: '', statement: '', section: '', appliesTo: '', sortKey: t.displayOrder,
        })),
        ...classes.data.map(c => ({
          id: c.id, parentId: c.accountTypeId, level: 'class' as const, code: c.classCode, name: c.className,
          normalBalance: '', statement: statementLabel(c.reportType), section: '', appliesTo: '', sortKey: c.displayOrder,
        })),
        ...subtypes.data.map(s => ({
          id: s.id, parentId: s.reportClassId && classIds.has(s.reportClassId) ? s.reportClassId : s.accountTypeId, level: 'subtype' as const,
          code: s.subtypeCode, name: s.subtypeName, normalBalance: s.normalBalance === 'DR' ? 'Debit' : s.normalBalance === 'CR' ? 'Credit' : s.normalBalance,
          statement: statementLabel(s.reportType), section: humanize(s.reportSection), appliesTo: appliesTo(s.countryCodes, s.legalForms), sortKey: s.displayOrder,
        })),
      ]);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not load the account classification.');
    } finally {
      setLoading(false);
    }
  }, [allCountries]);

  useEffect(() => { load(); }, [load]);

  // A search keeps the matching rows and everything above them.
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return nodes;
    const byId = new Map(nodes.map(n => [n.id, n]));
    const keep = new Set<string>();
    nodes.filter(n => n.name.toLowerCase().includes(q) || n.code.toLowerCase().includes(q)).forEach(n => {
      let cur: Node | undefined = n;
      while (cur && !keep.has(cur.id)) { keep.add(cur.id); cur = cur.parentId ? byId.get(cur.parentId) : undefined; }
    });
    return nodes.filter(n => keep.has(n.id));
  }, [nodes, search]);

  const columns: TreeGridColumn<Node>[] = useMemo(() => [
    { id: 'name', header: 'Name', accessorKey: 'name', size: 380, isTreeColumn: true,
      cell: ({ row }) => <span style={{ fontWeight: row.original.level === 'type' ? 700 : row.original.level === 'class' ? 600 : 400 }}>{row.original.name}</span> },
    { id: 'code', header: 'Code', accessorKey: 'code', size: 220 },
    { id: 'level', header: 'Level', size: 130, cell: ({ row }) => ({ type: 'Account type', class: 'Report class', subtype: 'Subtype' })[row.original.level] },
    { id: 'statement', header: 'Statement', accessorKey: 'statement', size: 150 },
    { id: 'section', header: 'Section', accessorKey: 'section', size: 180 },
    { id: 'normalBalance', header: 'Normal balance', accessorKey: 'normalBalance', size: 140 },
    { id: 'appliesTo', header: 'Applies to', accessorKey: 'appliesTo', size: 200 },
  ], []);

  const actionButtons: ActionButton[] = [actionButtonFactory.refresh(load)];

  return (
    <EnterpriseScreenShell
      title="Account Types"
      description="How ledgers are classified: account type, report class and subtype (shared by every entity; read-only)"
      icon={moduleIcon && (IconSystem as any)[moduleIcon]
        ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
        : <IconSystem.folder size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: search, onChange: setSearch, placeholder: 'Search types, classes or subtypes...' }}
      customToolbarItems={
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.8125rem', cursor: 'pointer', whiteSpace: 'nowrap' }}
          title="Off: the subtypes of the selected entity's country and legal form. On: every subtype.">
          <input type="checkbox" checked={allCountries} onChange={e => setAllCountries(e.target.checked)} /> All countries
        </label>
      }
    >
      <div className="data-grid-container" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <SyutTreeGrid data={shown} columns={columns} parentKey="parentId" sortKey="sortKey"
          showConnectorLines showNodeIcons defaultExpandLevel={search ? -1 : 1}
          isLoading={loading} emptyMessage={loading ? 'Loading...' : 'Nothing matches.'} />
      </div>
    </EnterpriseScreenShell>
  );
};

export default AccountTypesScreen;
